import React from 'react';
import { Timeline, Typography, Progress, Descriptions, Collapse, Space, Row, Col } from 'antd';
import { Sample } from '../../../../../types/lab/SampleTypes';
import {
	CheckCircleOutlined,
	ClockCircleOutlined,
	ExclamationCircleOutlined,
	PlayCircleOutlined,
	PauseCircleOutlined,
} from '@ant-design/icons';
import { SectionCard, StatusTag, EmptyState } from '../../../../../components/shared';
import dayjs from 'dayjs';

const { Text } = Typography;

interface ProtocolsTabProps {
	sample: Sample;
}

const ProtocolsTab: React.FC<ProtocolsTabProps> = ({ sample }) => {
	const getStatusIcon = (status: string) => {
		switch (status?.toLowerCase()) {
			case 'completed': return <CheckCircleOutlined style={{ color: 'var(--mg-success)' }} />;
			case 'in_progress': case 'running': return <PlayCircleOutlined style={{ color: 'var(--mg-primary)' }} />;
			case 'on_hold': case 'paused': return <PauseCircleOutlined style={{ color: 'var(--mg-warning)' }} />;
			case 'failed': return <ExclamationCircleOutlined style={{ color: 'var(--mg-danger)' }} />;
			default: return <ClockCircleOutlined style={{ color: 'var(--mg-text-disabled)' }} />;
		}
	};

	const formatDuration = (minutes: number) => {
		if (minutes < 60) return `${minutes}m`;
		const hours = Math.floor(minutes / 60);
		const remainingMinutes = minutes % 60;
		return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
	};

	const formatDate = (dateString: string | null | undefined) => {
		if (!dateString) return 'N/A';
		return dayjs(dateString).format('MMM DD, YYYY HH:mm');
	};

	const getProtocolProgress = (instance: any) => {
		if (!instance.protocol?.steps?.length) return 0;
		const completedSteps = instance.step_data?.filter((step: any) => step.status === 'completed').length || 0;
		return Math.round((completedSteps / instance.protocol.steps.length) * 100);
	};

	if (!sample.protocolInstances || sample.protocolInstances.length === 0) {
		return <EmptyState title="No protocols" description="No protocol instances found for this sample" />;
	}

	const completedCount = sample.protocolInstances.filter(p => p.status === 'completed').length;
	const overallProgress = Math.round((completedCount / sample.protocolInstances.length) * 100);

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
			{/* Protocol Overview */}
			<SectionCard title="Protocol Execution Overview">
				<Row gutter={[16, 16]}>
					<Col span={8} style={{ textAlign: 'center' }}>
						<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)', display: 'block' }}>Total Protocols</Text>
						<div style={{ fontSize: 18, fontWeight: 700, color: 'var(--mg-text)' }}>{sample.protocolInstances.length}</div>
					</Col>
					<Col span={8} style={{ textAlign: 'center' }}>
						<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)', display: 'block' }}>Completed</Text>
						<div style={{ fontSize: 18, fontWeight: 700, color: 'var(--mg-success)' }}>{completedCount}</div>
					</Col>
					<Col span={8} style={{ textAlign: 'center' }}>
						<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)', display: 'block' }}>Overall Progress</Text>
						<div style={{ fontSize: 18, fontWeight: 700, color: 'var(--mg-text)' }}>{overallProgress}%</div>
					</Col>
				</Row>
			</SectionCard>

			{/* Protocol Timeline */}
			<SectionCard title="Execution Timeline">
				<Timeline mode="left" style={{ paddingTop: 8 }}>
					{sample.protocolInstances.map((instance) => (
						<Timeline.Item key={instance.id} dot={getStatusIcon(instance.status)}>
							<div style={{ paddingBottom: 16 }}>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
									<div>
										<Text strong style={{ fontSize: 13 }}>{instance.protocol?.name}</Text>
										<br />
										<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)' }}>
											Version {instance.protocol?.version} • {instance.protocol?.steps?.length || 0} steps
										</Text>
									</div>
									<Space direction="vertical" align="end" size="small">
										<StatusTag status={instance.status} />
										<Progress percent={getProtocolProgress(instance)} size="small" style={{ minWidth: 100 }} />
									</Space>
								</div>

								<Descriptions size="small" column={2} colon={false}>
									<Descriptions.Item label={<Text style={{ fontSize: 12 }}>Started</Text>}>
										<Text style={{ fontSize: 12 }}>{formatDate(instance.started_at)}</Text>
									</Descriptions.Item>
									<Descriptions.Item label={<Text style={{ fontSize: 12 }}>Completed</Text>}>
										<Text style={{ fontSize: 12 }}>{formatDate(instance.completed_at)}</Text>
									</Descriptions.Item>
									<Descriptions.Item label={<Text style={{ fontSize: 12 }}>Duration</Text>}>
										<Text style={{ fontSize: 12 }}>
											{instance.total_processing_time ? formatDuration(Math.round(instance.total_processing_time * 60)) : 'N/A'}
										</Text>
									</Descriptions.Item>
									<Descriptions.Item label={<Text style={{ fontSize: 12 }}>Assigned To</Text>}>
										<Text style={{ fontSize: 12 }}>User ID: {instance.assigned_to || 'N/A'}</Text>
									</Descriptions.Item>
								</Descriptions>

								{instance.protocol?.description && (
									<div style={{ marginTop: 8 }}>
										<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)' }}>{instance.protocol.description}</Text>
									</div>
								)}
							</div>
						</Timeline.Item>
					))}
				</Timeline>
			</SectionCard>

			{/* Detailed Protocol Information */}
			<SectionCard title="Protocol Details">
				<Collapse size="small" ghost>
					{sample.protocolInstances.map((instance) => (
						<Collapse.Panel
							key={instance.id}
							header={
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
									<Space>
										{getStatusIcon(instance.status)}
										<Text style={{ fontSize: 13, fontWeight: 500 }}>{instance.protocol?.name}</Text>
									</Space>
									<Space>
										<StatusTag status={instance.status} />
										<Text style={{ fontSize: 12 }}>{getProtocolProgress(instance)}% Complete</Text>
									</Space>
								</div>
							}
						>
							<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
								<Descriptions size="small" column={3} colon={false} bordered>
									<Descriptions.Item label={<Text style={{ fontSize: 12, fontWeight: 500 }}>Protocol ID</Text>}>
										<Text style={{ fontSize: 12 }}>{instance.protocol_id}</Text>
									</Descriptions.Item>
									<Descriptions.Item label={<Text style={{ fontSize: 12, fontWeight: 500 }}>Group ID</Text>}>
										<Text style={{ fontSize: 12 }}>{instance.group_id}</Text>
									</Descriptions.Item>
									<Descriptions.Item label={<Text style={{ fontSize: 12, fontWeight: 500 }}>Current Step</Text>}>
										<Text style={{ fontSize: 12 }}>{instance.current_step}</Text>
									</Descriptions.Item>
									<Descriptions.Item label={<Text style={{ fontSize: 12, fontWeight: 500 }}>Completion</Text>}>
										<Text style={{ fontSize: 12 }}>{instance.completion_percentage || 0}%</Text>
									</Descriptions.Item>
									<Descriptions.Item label={<Text style={{ fontSize: 12, fontWeight: 500 }}>Est. Completion</Text>}>
										<Text style={{ fontSize: 12 }}>{formatDate(instance.estimated_completion)}</Text>
									</Descriptions.Item>
								</Descriptions>

								{/* Protocol Steps */}
								{instance.protocol?.steps && (
									<div>
										<Text style={{ fontSize: 13, fontWeight: 500 }}>Protocol Steps</Text>
										<div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
											{instance.protocol.steps.map((step) => {
												const stepExecution = instance.step_data?.find((sd: any) => sd.step_id === step.id);
												return (
													<SectionCard key={step.id}>
														<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
															<div style={{ flex: 1 }}>
																<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
																	<Text style={{ fontSize: 12, fontWeight: 500 }}>Step {step.step_number}: {step.name}</Text>
																	{stepExecution && <StatusTag status={stepExecution.status} />}
																</div>
																{step.description && (
																	<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)', display: 'block', marginBottom: 4 }}>
																		{step.description}
																	</Text>
																)}
																<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)' }}>
																	Type: {step.step_type} • Est. Duration: {step.duration_estimate || 0}min
																</Text>
															</div>
															{stepExecution && (
																<div style={{ textAlign: 'right' }}>
																	<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)', display: 'block' }}>Started: {formatDate(stepExecution.started_at)}</Text>
																	<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)', display: 'block' }}>Completed: {formatDate(stepExecution.completed_at)}</Text>
																</div>
															)}
														</div>

														{step.method && step.method.length > 0 && (
															<div style={{ marginTop: 8 }}>
																<Text style={{ fontSize: 12, fontWeight: 500 }}>Methods:</Text>
																<ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
																	{step.method.map((method, idx) => (
																		<li key={idx} style={{ fontSize: 12, color: 'var(--mg-text-secondary)' }}>
																			{method.name}{method.description && ` - ${method.description}`}
																		</li>
																	))}
																</ul>
															</div>
														)}

														{stepExecution?.notes && (
															<div style={{ marginTop: 8 }}>
																<Text style={{ fontSize: 12, fontWeight: 500 }}>Notes:</Text>
																<Text style={{ fontSize: 12, color: 'var(--mg-text-secondary)', display: 'block', marginTop: 4 }}>
																	{stepExecution.notes}
																</Text>
															</div>
														)}
													</SectionCard>
												);
											})}
										</div>
									</div>
								)}
							</div>
						</Collapse.Panel>
					))}
				</Collapse>
			</SectionCard>
		</div>
	);
};

export default ProtocolsTab;
